<template lang="pug">
.product-fields
  GeneralSettingsPanel(
    ref="generalSettingsPanel"
    v-model="product"
    :resourceOptions="resources"
    :availableSegments="availableSegments"
    :vuelidate="vuelidate"
    :disabled="disabled"
    :showSecondColumn="showSecondColumn"
    :enableProductDurationPerParticipant="enableProductDurationPerParticipant"
    )
  SchedulerPanel(
    v-model="product"
    :selectedResource="selectedResource"
    :vuelidate="vuelidate"
    :disabled="disabled"
    :enableProductDurationPerParticipant="enableProductDurationPerParticipant"
    @schedulingBehaviorSelected="x => $emit('schedulingBehaviorSelected', x)"
    )
  PriceConfigurationPanel(
    :product="product"
    :selectedResource="selectedResource"
    :resourceSegments="forEditorModel.ResourceSegments"
    :vuelidate="vuelidate"
    :disabled="disabled"
    :taxOptions="taxOptions"
    :show-accounting-fields="showAccountingFields"
    :ledgerAccounts="ledgerAccounts"
    :deals="forEditorModel.RelatedProductGroups"
    )
  WebsiteContentPanel(
    v-model="product"
    :vuelidate="vuelidate"
    :disabled="disabled"
    :showSecondColumn="showSecondColumn"
    :activeCulture="activeCulture"
    )
  CustomFieldsPanel(
    v-if="customFieldDefinitions && customFieldDefinitions.length !== 0"
    title="Custom fields"
    :customFields="product.CustomFields"
    :customFieldDefinitions="customFieldDefinitions"
    :customFieldsInVuelidate="vuelidate.product.CustomFields"
    :showSecondColumn="showSecondColumn"
    )
  // Integration fields
  CustomFieldsPanel(
    v-if="integrationFieldDefinitionsList && integrationFieldDefinitionsList.length !== 0"
    v-for="(integrationFieldDefinition, index) in integrationFieldDefinitionsList"
    :key="index"
    :title="integrationFieldDefinition.IntegrationDisplayName"
    :customFields="getIntegrationFields(integrationFieldDefinition.IntegrationCode)"
    :customFieldDefinitions="getIntegrationFieldDefinitions(integrationFieldDefinition.IntegrationCode)"
    :customFieldsInVuelidate="vuelidate.product.IntegrationFields[index].Fields"
    :showSecondColumn="showSecondColumn"
    )
</template>

<script>
import { propEq, isEmpty, prop, pipe, find, filter, keys, propOr } from 'ramda'
import GeneralSettingsPanel from '@v/inventory/products/edit/ProductFields/GeneralSettingsPanel.vue'
import SchedulerPanel from '@v/inventory/products/edit/ProductFields/SchedulerPanel.vue'
import PriceConfigurationPanel from '@v/inventory/products/edit/ProductFields/PriceConfigurationPanel.vue'
import WebsiteContentPanel from '@v/inventory/products/edit/ProductFields/WebsiteContentPanel.vue'
import CustomFieldsPanel from '@v/inventory/products/edit/ProductFields/CustomFieldsPanel.vue'
import modelMixin from '@v/mixins/modelMixin.js'
import { validateProps } from '@v/util/propValidators.js'
import {
  mergeIntegrationFields,
  generateIntegrationFieldsByDefinitions,
  mapCustomFieldsByDefinition,
} from '@/vue/inventory/products/edit/utils/integrationFieldsHelpers.js'

export default {
  name: 'ProductFields',
  components: {
    GeneralSettingsPanel,
    PriceConfigurationPanel,
    WebsiteContentPanel,
    CustomFieldsPanel,
    SchedulerPanel,
  },
  mixins: [modelMixin({ type: Object, name: 'product', required: true })],
  props: {
    forEditorModel: {
      type: Object,
      required: true,
      validators: validateProps([
        'Product',
        'CustomFieldDefinitions',
        'IntegrationFieldDefinitionsList',
        'ResourceSegments',
        'SegmentlessProduct',
        'RelatedProductGroups',
        'BusinessUnitTaxes',
        'LedgerAccounts',
        'FeatureFlags',
        'ProductFeeds',
        'Resources',
      ]),
    },
    showAccountingFields: Boolean,
    disabled: Boolean,
    showSecondColumn: Boolean,
    enableProductDurationPerParticipant: Boolean,
    vuelidate: {
      type: Object,
      required: true,
    },
    selectedResource: Object,
    activeCulture: Object,
  },
  computed: {
    resources() {
      return this.forEditorModel.Resources
    },
    availableSegments() {
      return this.forEditorModel.ResourceSegments.find(
        propEq('ResourceKey', this.product.ResourceKey),
      )
    },
    taxOptions() {
      return this.forEditorModel.BusinessUnitTaxes[0]?.AvailableTax || []
    },
    ledgerAccounts() {
      return this.forEditorModel.LedgerAccounts
    },
    customFieldDefinitions() {
      return this.forEditorModel.CustomFieldDefinitions
    },
    integrationFieldDefinitionsList() {
      return this.forEditorModel.IntegrationFieldDefinitionsList
    },
  },
  methods: {
    getIntegrationFields(integrationCode) {
      const filterIntegrations = filter(propEq('IntegrationCode', integrationCode))
      const getIntegrationByKey = x => filterIntegrations(this.product.IntegrationFields)[x]
      return pipe(
        filterIntegrations,
        keys,
        propOr(null, 0),
        getIntegrationByKey,
        prop('Fields'),
      )(this.product.IntegrationFields)
    },
    getIntegrationFieldDefinitions(integrationCode) {
      const integrationFieldsDefinitions = pipe(
        find(propEq('IntegrationCode', integrationCode)),
        prop('FieldDefinitions'),
      )(this.integrationFieldDefinitionsList)
      if (!integrationFieldsDefinitions) {
        console.error(`No definitions found for ${integrationCode}`)
      }
      return integrationFieldsDefinitions
    },
  },
  beforeMount() {
    const generatedIntegrationFields = generateIntegrationFieldsByDefinitions(
      this.integrationFieldDefinitionsList,
    )
    this.product.IntegrationFields = mergeIntegrationFields(
      generatedIntegrationFields,
      this.product.IntegrationFields,
    )
    if (isEmpty(this.product.CustomFields)) {
      this.product.CustomFields = mapCustomFieldsByDefinition(this.customFieldDefinitions)
    }
  },
}
</script>

<style lang="stylus" scoped>
@import '~@v/variables.styl'

pushSize = space-S

.product-fields
  h2
    {fontSizeHeading2}

  h3
    {fontSizeHeading3}

  .columns
    display grid
    grid-template-columns 1fr 1fr
    column-gap 30px

  .checkboxes
    border none

  .briq-form-element, .margin-push
    margin-bottom pushSize

  .image-alignment .option
    display inline-block

    .container
      padding-right 24px

  .subtitle
    padding-top 4px
    margin-top -(pushSize)
    {typeSmall}

  .no-margin
    margin 0

  .validation-message
    color validationRed
    font-weight bold
</style>
